import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
  getAssociatedTokenAddress,
} from '@solana/spl-token'
import { BN, web3 } from 'fbonds-core'

import { FRAGSOL_MINT, WFRAGSOL_MINT } from '.'

type GetMintWfragSolInstructions = (params: {
  solAmount: BN
  wfragSolAmount: BN
  walletPublicKey: web3.PublicKey
}) => Promise<{ instructions: web3.TransactionInstruction[] }>

const SYSVAR_INSTRUCTIONS = new web3.PublicKey('Sysvar1nstructions1111111111111111111111111')

const TOKEN_2022_PROGRAM = new web3.PublicKey('TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb')
const FRAGMETRIC_PROGRAM = new web3.PublicKey('fragnAis7Bp6FTsMoa6YcH8UffhEw43Ph79qAiK3iF3')
const EVENT_AUTHORITY = new web3.PublicKey('ELPv21PLEZ3q7SXyKtKLCvECEcbUcqVSZStCBb8dJXvB')
const REWARD_ACCOUNT = new web3.PublicKey('Cn9ziQ249Mg7h8Ef5XVaYsTHQ1Zw4DdyP73uXNJKZwAV')
const FUND_ACCOUNT = new web3.PublicKey('3TK9fNePM4qdKC4dwvDe8Bamv14prDqdVfuANxPeiryb')
const USER_DEPOSIT_SOL_REMAINING_ACCOUNTS = [
  new web3.PublicKey('stk9ApL5HeVAwPLr3TLhDXdZS8ptVu7zp6ov8HFDuMi'),
  new web3.PublicKey('Jito4APyf642JPZPx3hGc6WWJ8zPKtRbRs4P815Awbb'),
  new web3.PublicKey('8szGkuLTAux9XMgZ2vtY39jVSowEcpBfFfD8hXSEqdGC'),
  new web3.PublicKey('Hr9pzexrBge3vgmBNRR8u42CNQgBXdHm4UkUN2DH4a7r'),
  new web3.PublicKey('2aMLkB5p5gVvCwKkdSo5eZAL1WwhZbxezQr1wxiynRhq'),
  new web3.PublicKey('AkbZvKxUAxMKz92FF7g5k2YLCJftg8SnYEPWdmZTt3mp'),
  new web3.PublicKey('HR1ANmDHjaEhknvsTaK48M5xZtbBiwNdXM5NTiWhAb4S'),
  new web3.PublicKey('BuMRVW5uUQqJmguCk4toGh7DB3CcJt6dk64JiUMdYS22'),
  new web3.PublicKey('pyZMBjpWsVjKANAYK5mpNbKiws2krjRPZ2N2UYCSnbP'),
  new web3.PublicKey('8VpRhuxa7sUUepdY3kQiTmX9rS5vx4WgaXiAnXq4KCtr'),
]

export const getMintWfragSolInstructions: GetMintWfragSolInstructions = async ({
  solAmount,
  wfragSolAmount,
  walletPublicKey,
}) => {
  const mintIxns: Array<web3.TransactionInstruction> = []

  //? 1. Create ATA fro fragSOL / wallet
  const [walletFragSolAta] = web3.PublicKey.findProgramAddressSync(
    [walletPublicKey.toBuffer(), TOKEN_2022_PROGRAM.toBuffer(), FRAGSOL_MINT.toBuffer()],
    ASSOCIATED_TOKEN_PROGRAM_ID,
  )
  const createIdempotentWalletFragSolAtaIxn = createATAInstructionIdempotent({
    ata: walletFragSolAta,
    payer: walletPublicKey,
    walletAddress: walletPublicKey,
    splTokenMintAddress: FRAGSOL_MINT,
    tokenProgramId: TOKEN_2022_PROGRAM,
  })
  mintIxns.push(createIdempotentWalletFragSolAtaIxn)

  //? 2. Fragmetric: user_create_fund_account_idempotent
  const [userFundAccount] = web3.PublicKey.findProgramAddressSync(
    [
      Buffer.from(new Uint8Array([117, 115, 101, 114, 95, 102, 117, 110, 100])),
      FRAGSOL_MINT.toBuffer(),
      walletPublicKey.toBuffer(),
    ],
    FRAGMETRIC_PROGRAM,
  )

  const userCreateFundAccountIdempotentIxn = new web3.TransactionInstruction({
    keys: [
      { pubkey: walletPublicKey, isSigner: true, isWritable: true }, // User
      { pubkey: web3.SystemProgram.programId, isSigner: false, isWritable: false }, // System Program
      { pubkey: FRAGSOL_MINT, isSigner: false, isWritable: true }, // Receipt Token Mint
      { pubkey: TOKEN_2022_PROGRAM, isSigner: false, isWritable: false }, // Receipt Token Program (Token 2022)
      { pubkey: walletFragSolAta, isSigner: false, isWritable: true }, // User Receipt Token Account
      { pubkey: userFundAccount, isSigner: false, isWritable: true }, // User Fund Account
      { pubkey: EVENT_AUTHORITY, isSigner: false, isWritable: false },
      { pubkey: FRAGMETRIC_PROGRAM, isSigner: false, isWritable: false }, // Program
    ],
    programId: FRAGMETRIC_PROGRAM,
    data: userCreateFundAccountIdempotentPayload(),
  })
  mintIxns.push(userCreateFundAccountIdempotentIxn)

  //? 3. Fragmetric: user_create_reward_account_idempotent
  const [userRewardAccount] = web3.PublicKey.findProgramAddressSync(
    [
      Buffer.from(new Uint8Array([117, 115, 101, 114, 95, 114, 101, 119, 97, 114, 100])),
      FRAGSOL_MINT.toBuffer(),
      walletPublicKey.toBuffer(),
    ],
    FRAGMETRIC_PROGRAM,
  )

  const userCreateRewardAccountIdempotentIxn = new web3.TransactionInstruction({
    keys: [
      { pubkey: walletPublicKey, isSigner: true, isWritable: true }, // User
      { pubkey: web3.SystemProgram.programId, isSigner: false, isWritable: false }, // System Program
      { pubkey: FRAGSOL_MINT, isSigner: false, isWritable: true }, // Receipt Token Mint
      { pubkey: TOKEN_2022_PROGRAM, isSigner: false, isWritable: false }, // Receipt Token Program (Token 2022)
      { pubkey: walletFragSolAta, isSigner: false, isWritable: true }, // User Receipt Token Account
      {
        pubkey: REWARD_ACCOUNT,
        isSigner: false,
        isWritable: true,
      }, // Reward Account
      { pubkey: userRewardAccount, isSigner: false, isWritable: true }, // User Reward Account
      { pubkey: EVENT_AUTHORITY, isSigner: false, isWritable: false }, // Event Authority
      { pubkey: FRAGMETRIC_PROGRAM, isSigner: false, isWritable: false }, // Program
    ],
    programId: FRAGMETRIC_PROGRAM,
    data: userCreateRewardAccountIdempotentPayload(),
  })
  mintIxns.push(userCreateRewardAccountIdempotentIxn)

  //? 4. Fragmetric: user_deposit_sol
  const userDepositSolIxn = new web3.TransactionInstruction({
    keys: [
      { pubkey: walletPublicKey, isSigner: true, isWritable: true }, // User
      { pubkey: web3.SystemProgram.programId, isSigner: false, isWritable: false }, // System Program
      { pubkey: TOKEN_2022_PROGRAM, isSigner: false, isWritable: false }, // Receipt Token Program (Token 2022)
      { pubkey: FRAGSOL_MINT, isSigner: false, isWritable: true }, // Receipt Token Mint

      {
        pubkey: new web3.PublicKey('BmZ6UY8ZoKX2VMkYrnWjzZ9Eh6fR1fiQTScvoZRvzGm3'),
        isSigner: false,
        isWritable: true,
      }, // Receipt Token Lock Account
      {
        pubkey: walletFragSolAta,
        isSigner: false,
        isWritable: true,
      }, // User Receipt Token Account

      {
        pubkey: FUND_ACCOUNT,
        isSigner: false,
        isWritable: true,
      }, // Fund Account
      {
        pubkey: new web3.PublicKey('3H22A3T3CMyoGzAURZ4szV5Hmt64Dooo5g9Ns8h1kYy7'),
        isSigner: false,
        isWritable: true,
      }, // Fund Reserve Account

      { pubkey: userFundAccount, isSigner: false, isWritable: true }, //  User Fund Account
      { pubkey: REWARD_ACCOUNT, isSigner: false, isWritable: true }, // Reward Account
      { pubkey: userRewardAccount, isSigner: false, isWritable: true }, // User Reward Account

      {
        pubkey: SYSVAR_INSTRUCTIONS,
        isSigner: false,
        isWritable: false,
      }, // Instructions Sysvar
      { pubkey: EVENT_AUTHORITY, isSigner: false, isWritable: false }, // Event Authority
      { pubkey: FRAGMETRIC_PROGRAM, isSigner: false, isWritable: false }, // Program

      ...USER_DEPOSIT_SOL_REMAINING_ACCOUNTS.map((pubkey) => ({
        pubkey,
        isSigner: false,
        isWritable: false,
      })),
    ],
    programId: FRAGMETRIC_PROGRAM,
    data: userDepositSolPayload(solAmount.toNumber()),
  })
  mintIxns.push(userDepositSolIxn)

  //? 4. Fragmetric: user_wrap_receipt_token
  const walletWFragSolAta = await getAssociatedTokenAddress(WFRAGSOL_MINT, walletPublicKey)

  const userWrapReceiptTokenIxn = new web3.TransactionInstruction({
    keys: [
      { pubkey: walletPublicKey, isSigner: true, isWritable: true }, // User
      {
        pubkey: new web3.PublicKey('HWdpqHAJ1U3hmFpqJg5tJVrjaCJ7PuzB6j1VQf5VDqgJ'),
        isSigner: false,
        isWritable: false,
      }, // Fund Wrap Account
      { pubkey: TOKEN_2022_PROGRAM, isSigner: false, isWritable: false }, // Receipt Token Program (Token 2022)
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false }, // Wrapped Token Program
      { pubkey: FRAGSOL_MINT, isSigner: false, isWritable: true }, // Receipt Token Mint
      { pubkey: WFRAGSOL_MINT, isSigner: false, isWritable: true }, // Wrapped Token Mint
      {
        pubkey: walletFragSolAta,
        isSigner: false,
        isWritable: true,
      }, // User Receipt Token Account
      {
        pubkey: new web3.PublicKey('B3iqP1N6xUAzHrJQCeUoZH1SrkADNxaLZfzGpzKhEZ3L'),
        isSigner: false,
        isWritable: true,
      }, // Receipt Token Wrap Account
      {
        pubkey: walletWFragSolAta,
        isSigner: false,
        isWritable: true,
      }, // User Wrapped Token Account
      {
        pubkey: FUND_ACCOUNT,
        isSigner: false,
        isWritable: true,
      }, // Fund Account
      {
        pubkey: userFundAccount,
        isSigner: false,
        isWritable: true,
      }, // User Fund Account
      {
        pubkey: REWARD_ACCOUNT,
        isSigner: false,
        isWritable: true,
      }, // Reward Account
      {
        pubkey: userRewardAccount,
        isSigner: false,
        isWritable: true,
      }, // User Reward Account
      {
        pubkey: new web3.PublicKey('Hem9aZfHMhz7VTG76zoQ7fF3d85mRwEuKDkh6ytVfeem'),
        isSigner: false,
        isWritable: true,
      }, // Fund Wrap Account Reward Account
      {
        pubkey: EVENT_AUTHORITY,
        isSigner: false,
        isWritable: true,
      }, // Event Authority
      { pubkey: FRAGMETRIC_PROGRAM, isSigner: false, isWritable: false }, // Program
    ],
    programId: FRAGMETRIC_PROGRAM,
    data: userWrapFragSolPayload(wfragSolAmount.toNumber()),
  })
  mintIxns.push(userWrapReceiptTokenIxn)

  return {
    instructions: mintIxns,
  }
}

const userCreateFundAccountIdempotentPayload = () => {
  const discriminator = new Uint8Array([18, 13, 182, 219, 153, 232, 60, 152])
  const amountBytes = new Uint8Array(4)
  const payloadBytes = new Uint8Array([...discriminator, ...amountBytes])
  return Buffer.from(payloadBytes)
}

const userCreateRewardAccountIdempotentPayload = () => {
  const discriminator = new Uint8Array([143, 244, 109, 23, 228, 116, 145, 26])
  const amountBytes = new Uint8Array(4)
  const payloadBytes = new Uint8Array([...discriminator, ...amountBytes])
  return Buffer.from(payloadBytes)
}

const userDepositSolPayload = (amount: number) => {
  const discriminator = new Uint8Array([9, 201, 63, 79, 105, 75, 147, 47])

  const amountBytes = new Uint8Array(new BigUint64Array([BigInt(amount)]).buffer)
  const depositMetadataPlaceholder = new Uint8Array(55)

  const payloadBytes = new Uint8Array([
    ...discriminator,
    ...amountBytes,
    ...depositMetadataPlaceholder,
  ])

  return Buffer.from(payloadBytes)
}

const userWrapFragSolPayload = (amount: number) => {
  const discriminator = new Uint8Array([206, 52, 33, 7, 205, 206, 195, 24])

  const amountBytes = new Uint8Array(new BigUint64Array([BigInt(amount)]).buffer)

  const payloadBytes = new Uint8Array([...discriminator, ...amountBytes])

  return Buffer.from(payloadBytes)
}

const createATAInstructionIdempotent = (params: {
  ata: web3.PublicKey
  payer: web3.PublicKey
  walletAddress: web3.PublicKey
  splTokenMintAddress: web3.PublicKey
  tokenProgramId: web3.PublicKey
}) => {
  const { ata, payer, walletAddress, splTokenMintAddress, tokenProgramId } = params

  return new web3.TransactionInstruction({
    keys: [
      {
        pubkey: payer,
        isSigner: true,
        isWritable: true,
      },
      {
        pubkey: ata,
        isSigner: false,
        isWritable: true,
      },
      {
        pubkey: walletAddress,
        isSigner: false,
        isWritable: false,
      },
      {
        pubkey: splTokenMintAddress,
        isSigner: false,
        isWritable: false,
      },
      {
        pubkey: web3.SystemProgram.programId,
        isSigner: false,
        isWritable: false,
      },
      {
        pubkey: tokenProgramId,
        isSigner: false,
        isWritable: false,
      },
      {
        pubkey: web3.SYSVAR_RENT_PUBKEY,
        isSigner: false,
        isWritable: false,
      },
    ],
    programId: ASSOCIATED_TOKEN_PROGRAM_ID,
    data: Buffer.from([1]),
  })
}
